import {EnvironmentProviders, inject, LOCALE_ID, makeEnvironmentProviders} from '@angular/core';
import {HTTP_INTERCEPTORS, HttpClient, HttpHeaders} from '@angular/common/http';
import {TranslateLoader} from '@ngx-translate/core';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
import {forkJoin, Observable, of} from 'rxjs';
import {map, catchError} from 'rxjs/operators';
import {provideState} from '@ngrx/store';
import {
  AuthenticationService,
  BasicAuthInterceptor,
  CardataWizardCommunicationService,
  CardataWizardService,
  configReducer,
  ConfigService as CardataConfigService,
  DEFAULT_TIMEOUT,
  FilterService,
  GeneralSignalesStateService,
  HttpSearchService,
  ManualSearchSignalsStateService,
  NewVehicleSignalStoreService,
  PageService,
  PublishServiceDirective,
  ReviewSignalsStateService,
  searchReducer,
  SearchResultSignalsStateService,
  SnackbarService,
  StartSignalsStateService,
  TimeoutInterceptor,
  TranslationService,
  UiService,
  uiReducer
} from '@ucb2b/cardata-wizard';

/**
 * Custom TranslateLoader that delegates to the appropriate source:
 * - App locale codes (lowercase like 'de-AT', 'en') → loads from /assets/i18n/{lang}.json
 * - Library language codes (uppercase like 'DE', 'EN') → loads from TDS API
 *
 * When the library calls translate.use('DE'), this loader fetches TDS translations
 * AND merges in the app's own translations. This prevents the shared TranslateService
 * from losing app translation keys (e.g. for tabs) when the library switches the language.
 */
export class CardataTranslateLoader implements TranslateLoader {
  private httpLoader: TranslateHttpLoader;

  constructor(private http: HttpClient, private appLocale: string) {
    this.httpLoader = new TranslateHttpLoader(http);
  }

  getTranslation(lang: string): Observable<any> {
    if (lang === lang.toUpperCase() && /^[A-Z]{2,3}$/.test(lang)) {
      const baseurl = localStorage.getItem('cardata-svc-url');
      const tds$ = baseurl
        ? this.http.get<any>(`${baseurl}/api/v2/cardata/texts/${lang}`, {
            headers: new HttpHeaders()
              .set('Content-Type', 'application/json; charset=utf-8')
              .set('Accept', 'application/json; charset=utf-8'),
            responseType: 'json',
          })
        : of({});

      // Also load the app's translations so keys like TAB.VEHICLES still resolve
      const app$ = this.httpLoader.getTranslation(this.appLocale).pipe(
        catchError(() => of({})),
      );

      // App translations first, TDS overrides where keys overlap
      return forkJoin([app$, tds$]).pipe(
        map(([appTranslations, tdsTranslations]) => ({...appTranslations, ...tdsTranslations})),
      );
    }
    return this.httpLoader.getTranslation(lang);
  }
}

export function cardataTranslateLoaderFactory(http: HttpClient): CardataTranslateLoader {
  const locale = inject(LOCALE_ID);
  return new CardataTranslateLoader(http, locale);
}

/**
 * Provides all services, interceptors, NgRx feature states, and the custom
 * TranslateLoader required by @ucb2b/cardata-wizard.
 *
 * Usage in app.config.ts:
 *   provideCardataWizard()
 *
 * Still requires TranslateModule.forRoot() to be configured with the
 * exported cardataTranslateLoaderFactory (or CardataTranslateLoader).
 *
 * TODO: With the @ucb2b/cardata-wizard update to Angular 22, the library will
 * export its own provider function (e.g. provideCardataWizard). Once that update
 * is available, this file can be removed and replaced with the library's built-in
 * provider.
 */
export function provideCardataWizard(config?: {timeoutMs?: number}): EnvironmentProviders {
  const timeout = config?.timeoutMs ?? 30000;

  return makeEnvironmentProviders([
    // Services
    AuthenticationService,
    CardataWizardCommunicationService,
    CardataWizardService,
    CardataConfigService,
    FilterService,
    GeneralSignalesStateService,
    HttpSearchService,
    ManualSearchSignalsStateService,
    NewVehicleSignalStoreService,
    PageService,
    PublishServiceDirective,
    ReviewSignalsStateService,
    SearchResultSignalsStateService,
    SnackbarService,
    StartSignalsStateService,
    TranslationService,
    UiService,

    // Interceptors
    {provide: HTTP_INTERCEPTORS, useClass: BasicAuthInterceptor, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: TimeoutInterceptor, multi: true},
    {provide: DEFAULT_TIMEOUT, useValue: timeout},

    // NgRx feature states
    // Selectors use createFeatureSelector directly, so the feature state IS the reducer state
    provideState({name: 'cardataUiFeature', reducer: uiReducer as any}),
    provideState({name: 'cardataSearchFeature', reducer: searchReducer as any}),
    provideState({name: 'cardataConfigFeature', reducer: configReducer as any}),
  ]);
}
